import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { AppLayout } from "@/components/layout/AppLayout";
import AbayAIBridge from "@/components/ai/AbayAIBridge";
import AdminGuard from "@/components/admin/AdminGuard";
import AdminLayout from "@/components/admin/AdminLayout";
import Home from "@/pages/Home";

// Route-level code splitting: non-critical pages load on demand, shrinking the
// initial bundle. Home + layout shell + guards stay eager for a fast first paint.
const PlaceholderPage = lazy(() => import("@/pages/PlaceholderPage"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const BlogIndex = lazy(() => import("@/pages/blog/BlogIndex"));
const ArticlePage = lazy(() => import("@/pages/blog/ArticlePage"));
const HelpCenter = lazy(() => import("@/pages/help/HelpCenter"));
const HelpGuide = lazy(() => import("@/pages/help/HelpGuide"));
const Agents = lazy(() => import("@/pages/Agents"));
const MapSearch = lazy(() => import("@/pages/map-search/MapSearch"));
const LocationPage = lazy(() => import("@/pages/geo/LocationPage"));
const Developers = lazy(() => import("@/pages/Developers"));
const Investment = lazy(() => import("@/pages/Investment"));
const MortgageDashboard = lazy(() => import("@/pages/mortgage/MortgageDashboard"));
const LegalDocument = lazy(() => import("@/pages/legal/LegalDocument"));
const Safety = lazy(() => import("@/pages/Safety"));
const Login = lazy(() => import("@/pages/auth/Login"));
const Signup = lazy(() => import("@/pages/auth/Signup"));
const ForgotPassword = lazy(() => import("@/pages/auth/ForgotPassword"));
const UpdatePassword = lazy(() => import("@/pages/auth/UpdatePassword"));
const PropertyList = lazy(() => import("@/pages/properties/PropertyList"));
const PropertyDetail = lazy(() => import("@/pages/properties/PropertyDetail"));
const ListingWizard = lazy(() => import("@/pages/properties/ListingWizard"));
const Dashboard = lazy(() => import("@/pages/dashboard/Dashboard"));
const ProfileEditor = lazy(() => import("@/pages/profile/ProfileEditor"));
const AgencyManage = lazy(() => import("@/pages/profile/AgencyManage"));
const PublicProfile = lazy(() => import("@/pages/profile/PublicProfile"));
const AgencyProfile = lazy(() => import("@/pages/profile/AgencyProfile"));
const DeveloperProfile = lazy(() => import("@/pages/profile/DeveloperProfile"));
const MessagesInbox = lazy(() => import("@/pages/messages/MessagesInbox"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminApplications = lazy(() => import("@/pages/admin/AdminApplications"));
const AdminProperties = lazy(() => import("@/pages/admin/AdminProperties"));
const AdminContact = lazy(() => import("@/pages/admin/AdminContact"));
const AdminAI = lazy(() => import("@/pages/admin/AdminAI"));
const AdminBlog = lazy(() => import("@/pages/admin/AdminBlog"));
const AdminHelp = lazy(() => import("@/pages/admin/AdminHelp"));
const AdminAnalytics = lazy(() => import("@/pages/admin/AdminAnalytics"));
const AbayAIExperience = lazy(() => import("@/pages/ai/AbayAIExperience"));

const RouteFallback = () => (
  <div className="flex min-h-[60vh] items-center justify-center" data-testid="route-loading">
    <Loader2 className="h-6 w-6 animate-spin text-[#1E3A8A]" />
  </div>
);

const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Suspense fallback={<RouteFallback />}>
        <Routes location={location} key={location.pathname}>
        {/* Abay AI — unified full-screen experience, OUTSIDE AppLayout so there is
            no header/bottom-nav overlap. All AI entry points route here. */}
        <Route path="/abay-ai" element={<AbayAIExperience />} />

        {/* Admin Portal — separate layout, gated by admin role (Phase 3.8) */}
        <Route path="/admin" element={<AdminGuard><AdminLayout /></AdminGuard>}>
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="applications" element={<AdminApplications />} />
          <Route path="properties" element={<AdminProperties />} />
          <Route path="contact" element={<AdminContact />} />
          <Route path="ai" element={<AdminAI />} />
          <Route path="blog" element={<AdminBlog />} />
          <Route path="help" element={<AdminHelp />} />
          <Route path="analytics" element={<AdminAnalytics />} />
        </Route>

        <Route element={<AppLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/properties" element={<PropertyList />} />
          <Route path="/map-search" element={<MapSearch />} />
          <Route path="/ethiopia" element={<LocationPage />} />
          <Route path="/ethiopia/*" element={<LocationPage />} />
          <Route path="/properties/:id" element={<PropertyDetail />} />
          <Route path="/buy" element={<PropertyList deal="buy" />} />
          <Route path="/rent" element={<PropertyList deal="rent" />} />
          <Route path="/land" element={<PropertyList deal="land" />} />
          <Route path="/commercial" element={<PropertyList deal="commercial" />} />
          <Route path="/new-projects" element={<PlaceholderPage pageKey="new-projects" />} />
          <Route path="/invest" element={<Investment />} />
          <Route path="/mortgage" element={<MortgageDashboard />} />
          <Route path="/agents" element={<Agents />} />
          <Route path="/developers" element={<Developers />} />
          <Route path="/blog" element={<BlogIndex />} />
          <Route path="/blog/:slug" element={<ArticlePage />} />
          <Route path="/help" element={<HelpCenter />} />
          <Route path="/help/:slug" element={<HelpGuide />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/privacy" element={<LegalDocument slug="privacy" />} />
          <Route path="/terms" element={<LegalDocument slug="terms" />} />
          <Route path="/cookies" element={<LegalDocument slug="cookies" />} />
          <Route path="/safety" element={<Safety />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/update-password" element={<UpdatePassword />} />
          <Route path="/list-property" element={<ProtectedRoute><ListingWizard /></ProtectedRoute>} />
          <Route path="/list-property/:id/edit" element={<ProtectedRoute><ListingWizard /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/account/profile" element={<ProtectedRoute><ProfileEditor /></ProtectedRoute>} />
          <Route path="/account/agency" element={<ProtectedRoute><AgencyManage /></ProtectedRoute>} />
          <Route path="/u/:username" element={<PublicProfile />} />
          <Route path="/agency/:slug" element={<AgencyProfile />} />
          <Route path="/developer/:slug" element={<DeveloperProfile />} />
          <Route path="/messages" element={<ProtectedRoute><MessagesInbox /></ProtectedRoute>} />
          <Route path="*" element={<PlaceholderPage pageKey="notfound" />} />
        </Route>
      </Routes>
      </Suspense>
    </AnimatePresence>
  );
};

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AbayAIBridge />
            <AnimatedRoutes />
            <Toaster position="top-center" richColors />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
